import { NOTIFICATION_ICONS, NOTIFICATION_TYPES } from './constants';

/**
 * Transform raw notification data from API to UI-friendly format
 */
export const serializeNotification = (rawNotification) => {
  if (!rawNotification) return null;

  return {
    id: rawNotification.id || rawNotification._id || generateId(),
    title: rawNotification.title || rawNotification.subject || 'Notification',
    message: rawNotification.message || rawNotification.body || rawNotification.content || '',
    type: normalizeNotificationType(rawNotification.type || rawNotification.category || NOTIFICATION_TYPES.SYSTEM),
    read: Boolean(rawNotification.read || rawNotification.isRead || rawNotification.readAt),
    readAt: rawNotification.readAt || null,
    createdAt: rawNotification.createdAt || rawNotification.created_at || new Date().toISOString(),
    updatedAt: rawNotification.updatedAt || rawNotification.updated_at || null,
    icon: getNotificationIcon(rawNotification.type || rawNotification.category),
    action: rawNotification.action || null,
    actionUrl: rawNotification.actionUrl || rawNotification.action_url || rawNotification.link || null,
    metadata: rawNotification.metadata || rawNotification.data || {},
    priority: rawNotification.priority || 'normal',
    expiresAt: rawNotification.expiresAt || rawNotification.expires_at || null
  };
};

/**
 * Transform array of notifications
 */
export const serializeNotifications = (rawNotifications) => {
  if (!Array.isArray(rawNotifications)) return [];
  return rawNotifications.map(serializeNotification).filter(n => n !== null);
};

/**
 * Prepare notification data for marking as read
 */
export const serializeMarkReadRequest = (notificationId) => {
  return {
    notificationId: notificationId,
    readAt: new Date().toISOString()
  };
};

/**
 * Prepare notification data for deletion
 */
export const serializeDeleteRequest = (notificationId) => {
  return {
    notificationId: notificationId,
    deletedAt: new Date().toISOString()
  };
};

/**
 * Prepare filter data for API request
 */
export const serializeFilterRequest = (filters) => {
  return {
    type: filters.type || 'all',
    readStatus: filters.readStatus || 'all',
    limit: filters.limit || 50,
    offset: filters.offset || 0
  };
};

/**
 * Normalize notification type to standard types
 */
function normalizeNotificationType(type) {
  const typeMap = {
    'order': NOTIFICATION_TYPES.ORDER,
    'orders': NOTIFICATION_TYPES.ORDER,
    'commande': NOTIFICATION_TYPES.ORDER,
    'commandes': NOTIFICATION_TYPES.ORDER,
    'promotion': NOTIFICATION_TYPES.PROMOTION,
    'promo': NOTIFICATION_TYPES.PROMOTION,
    'deal': NOTIFICATION_TYPES.PROMOTION,
    'offer': NOTIFICATION_TYPES.PROMOTION,
    'system': NOTIFICATION_TYPES.SYSTEM,
    'app': NOTIFICATION_TYPES.SYSTEM,
    'payment': NOTIFICATION_TYPES.PAYMENT,
    'paiement': NOTIFICATION_TYPES.PAYMENT,
    'transaction': NOTIFICATION_TYPES.PAYMENT,
    'delivery': NOTIFICATION_TYPES.DELIVERY,
    'livraison': NOTIFICATION_TYPES.DELIVERY,
    'shipping': NOTIFICATION_TYPES.DELIVERY
  };

  const normalizedType = type?.toLowerCase();
  return typeMap[normalizedType] || NOTIFICATION_TYPES.SYSTEM;
}

/**
 * Get icon path for notification type
 */
function getNotificationIcon(type) {
  const normalizedType = normalizeNotificationType(type);
  return NOTIFICATION_ICONS[normalizedType] || NOTIFICATION_ICONS.default;
}

/**
 * Generate temporary ID for notifications without ID
 */
function generateId() {
  return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Format notification date for display
 */
export const formatNotificationDate = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date >= today) {
    // Today - show time
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } else if (date >= yesterday) {
    // Yesterday
    return 'Hier';
  } else if (date >= new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)) {
    // This week - show day name
    return date.toLocaleDateString('fr-FR', { weekday: 'long' });
  } else {
    // Older - show date
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  }
};

/**
 * Get notification group label (today, yesterday, older)
 */
export const getNotificationGroupLabel = (group) => {
  const labels = {
    today: "Aujourd'hui",
    yesterday: 'Hier',
    older: 'Plus anciens'
  };
  return labels[group] || group;
};
